<template>
  <article class="relative flex w-full h-full" :class="{ 'best-match-border': label, 'xl:scale-95': !label }" data-component="CardRecommendationSuggestion">
    <AtomLink
      :url="url"
      :class="{ 'border-solid border-4 border-app-primary-1-orange--bittersweet': label }"
      class="group grid w-full rounded-3xl overflow-hidden shadow-lg hover:shadow-none transform transition duration-200"
    >
      <section class="order-2 c-content relative row-span-1 col-span-6 flex">
        <div class="relative z-20 bg-white flex flex-1 flex-col justify-end px-5 pb-5">
          <div class="c-content__heading flex-auto flex flex-col">
            <h3
              v-if="title"
              class="mr-10 md:mr-5 text-base sm:text-lg lg:text-2xl font-semibold leading-snug text-black mb-2 order-3"
              :class="{ 'md:mb-3': body, 'md:mb-5': !body }"
            >
              {{ title }}
            </h3>
            <span v-if="when" class="z-10 text-black font-semibold text-sm mb-2 lg:text-xl leading-none block order-1">
              {{ d_(when, 'long-date') }}
            </span>
            <ul class="flex flex-wrap text-xs lg:text-sm font-medium leading-tight mb-4 order-2" :class="{ 'lg:mb-6': !body }">
              <li v-if="when" class="py-1 inline-flex items-center shrink-0">
                <time :datetime="d_(when, 'iso')" class="c-card-event-and-series__time lowercase">
                  {{ d_(when, 'short-time') }}
                  <template v-if="endDate"><span class="px-px lg:px-1">–</span>{{ d_(endDate, 'short-time') }} </template>
                </time>
              </li>
              <template v-if="location">
                <li v-if="when" role="presentation" class="my-1 border-r border-solid border-app-primary-1-red--fire-brick mx-2"></li>
                <li class="py-1">
                  <span>{{ location }}</span>
                </li>
              </template>
              <template v-if="minPrice">
                <li v-if="when || location" role="presentation" class="my-1 border-r border-solid border-app-primary-1-red--fire-brick mx-2"></li>
                <li class="py-1">
                  <span class="mr-2 lg:mr-5">{{ $t('card-event-and-series.card-price', { minPrice: $n(minPrice, 'currency') }) }}</span>
                </li>
              </template>
            </ul>
            <template v-if="body">
              <div class="leading-snug text-xs lg:text-sm mr-6 mb-4">
                <div v-if="stripTags(body).length < 195" v-html="body" />
                <div v-else v-html="truncate(stripTags(body), { length: 195 })" />
              </div>
            </template>
            <ul v-if="whenDescription" class="order-5 flex flex-wrap text-sm lg:text-base font-semibold leading-tight mb-2" :class="{ 'lg:mb-4': !body }">
              <li class="py-1">{{ whenDescription }}</li>
            </ul>
            <AtomAnimationsCircleLoader v-if="loading" class="w-12 h-12 order-6" />
          </div>
          <footer class="flex flex-wrap">
            <template v-if="url">
              <MoleculeButton class="mr-2 lg:hidden !font-normal" background-color="bg-app-primary-1-orange--bittersweet group-hover:bg-app-primary-1-red--fire-brick" size="large">
                {{ tixSale.saleIsAvailable ? $t('recommendation.suggestion-page.button-available') : $t('recommendation.suggestion-page.button-unavailable') }}
              </MoleculeButton>
              <MoleculeButton size="large" class="mr-4 hidden lg:flex !font-normal" background-color="bg-app-primary-1-orange--bittersweet group-hover:bg-black">
                {{ tixSale.saleIsAvailable ? $t('recommendation.suggestion-page.button-available') : $t('recommendation.suggestion-page.button-unavailable') }}
              </MoleculeButton>
            </template>
          </footer>
        </div>
      </section>
      <figure class="order-1 row-span-1 col-span-6 relative" :class="{ '!row-span-2': label }">
        <span
          v-if="label"
          class="absolute z-10 bg-app-primary-1-orange--bittersweet top-0 left-0 rounded-br-3xl text-white font-semibold text-sm lg:text-lg leading-none px-5 py-3 lg:px-8 lg:py-3"
        >
          {{ label }}
        </span>
        <MoleculePicture class="block w-full h-full object-cover group-hover:scale-110 group-focus:scale-110 transform transition duration-200" :image="image" />
      </figure>
    </AtomLink>
    <div
      v-if="eventId"
      class="z-40 text-white absolute top-4 right-4 xl:top-6 hover:cursor-pointer xl:right-6"
      @click="favoritesEnabled ? favoritesToggleEventId(eventId) : favoritesOpenOverlay(eventId)"
    >
      <AtomIconBookmarkFilled v-if="favoriteEventIConcertIds.includes(eventId)" class="w-8 h-8 mx-2" />
      <AtomIconBookmark v-else class="w-8 h-8 mx-2" />
    </div>
  </article>
</template>

<script setup lang="ts">
import { stripTags, truncate } from '@zicht/nuxt-util'

const props = defineProps({
  body: { type: String, required: false, default: undefined },
  endDate: { type: [String, Date], required: false, default: undefined },
  image: {
    type: Object as PropType<Record<string, any>>,
    required: false,
    default: () => ({
      _smUrl: '/images/fallback/345x206.png',
      _smWidth: 908,
      _smHeight: 545,
      _mdUrl: '/images/fallback/463x280.png',
      _mdWidth: 947,
      _mdHeight: 573,
    }),
  },
  imgAlt: { type: String, required: false, default: undefined },
  label: { type: String, required: false, default: undefined },
  discount: { type: String, required: false, default: undefined },
  location: { type: String, required: false, default: undefined },
  streamUrl: { type: String, required: false, default: undefined },
  title: { type: String, required: false, default: undefined },
  url: { type: String, required: false, default: undefined },
  when: { type: [String, Date], required: false, default: undefined },
  whenDescription: { type: String, required: false, default: undefined },
  works: { type: Array as PropType<any[]>, required: false, default: () => [] },

  // The card can be used as a placeholder while the underlying data is loading
  loading: { type: Boolean, required: false, default: false },

  // concert status from "IConcert"
  concertStatus: { type: String, required: false, default: '' },
  eventId: { type: Number, required: false, default: undefined },
  // Price information from `setupTixPricing`
  tixPricing: { type: Object as PropType<Record<string, any>>, required: false, default: () => ({}) },
  // Sale information from `setupTixSale`
  tixSale: { type: Object as PropType<Record<string, any>>, required: false, default: () => ({}) },
})

const minPrice = computed(() => props.tixPricing?.minPrice?.value)
const favoritesStore = useFavoritesStore()

const { d_ } = setupTranslationPrefixer('CardEventAndSeries')

const favoritesEnabled = computed(() => favoritesStore.enabled)
const favoriteEventIConcertIds = computed(() => favoritesStore.eventIConcertIds)

function favoritesToggleEventId(eventIConcertId: number) {
  favoritesStore.toggleEventIConcertId(eventIConcertId)
}

function favoritesOpenOverlay(eventIConcertId: number) {
  favoritesStore.openOverlayAndStoreId(eventIConcertId)
}
</script>

<style lang="scss" scoped>
$side-size-svg: 49px;

.c-content {
  @apply z-10;
  padding-top: calc(#{$side-size-svg} - 1px);
  margin-top: calc(-1 * (#{$side-size-svg} - 1px));

  &::before {
    content: '';
    background-image: url('/images/brand/BrandLyreCardHorizontal.svg');
    width: 84px;
    height: $side-size-svg;
    @apply absolute top-0 right-0 bg-no-repeat bg-cover;
  }

  &::after {
    content: '';
    width: calc(100% - 84px + 1px);
    height: $side-size-svg;
    @apply absolute top-0 left-0 bg-white;
  }
}

.c-content__heading {
  margin-top: calc(-1 * theme('spacing.6'));
}

.c-content__title {
  margin-top: calc(-1 * theme('spacing.6'));
}

.c-content-list-item {
  position: relative;
  padding-left: 12px;

  &::marker {
    display: none;
  }

  &::before {
    content: '·';
    position: absolute;
    top: -2px;
    left: 0;
    font-size: 20px;
    line-height: 1;
  }
}

//.best-match-border {
//  &::before {
//    content: '';
//    position: absolute;
//    width: 102%;
//    height: 102%;
//    top: -1%;
//    left: -1%;
//    @apply bg-app-primary-1-orange--bittersweet rounded-[1.6rem];
//  }
//}
</style>
